import { useState, useRef, useEffect } from "react";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import SwitchMenu from "./SwitchMenu";
import PlusMenu from "./PlusMenu";
import ModelKiezer from "./ModelKiezer";
import McpBevestigingen from "./McpBevestigingen";
import { ScrollArea } from "./ui/scroll-area";
import { staatOnderaan, magMeescrollen, gedragVoor } from "@/lib/meescrollen";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import MessageBubble from "./MessageBubble";
import MemoryProposal from "./MemoryProposal";
import MemoryNotice from "./MemoryNotice";
import VerlopenMelding from "./VerlopenMelding";
import { Send, PanelLeft, Clock, TrendingUp, Lightbulb, PenTool, GraduationCap, Code, Paperclip, X, GitBranch, FileText, Landmark, Check, QrCode, Sparkles, Globe, Square, RotateCw, Orbit } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { cn } from "../lib/utils";
import { PROVIDERS } from "../lib/providers";
import { toast } from "sonner";
import ProjectBadge from "./ProjectBadge";
import UniversumVenster from "./UniversumVenster";
import KoppelingenVenster from "./KoppelingenVenster";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { OPDRACHTEN, formulierVan, metTekst, naam as opdrachtNaam, passend, perGroep, voorbeeld as opdrachtVoorbeeld, zinMetTekst, zinZonderTekst } from "../lib/slashopdrachten";
import SlashWorkerFormulier from "./SlashWorkerFormulier";
import AgentMenu, { useAgentMenu } from "./AgentMenu";
import { useTaal } from "../contexts/TaalContext";

// GL212-7 (glasronde 0.9.212, 28 oktober 2026): de knoppen en hun voorbeelden volgen de taal van
// de app, net als menu en knoppen elders. Nederlands is de sleutel; het Engels staat in `taal-en.js`.
// De welkomstzin erboven blijft met opzet Engels (besluit Frank 11 oktober 2026).
const SUGGESTIONS = {
  strategize: {
    label: "Strategie",
    icon: TrendingUp,
    items: [
      "Maak klantreizen",
      "Analyseer bedrijfscijfers",
      "Ontwikkel een zakelijk ecosysteem",
      "Maak leveranciersbeoordelingen",
      "Ontwikkel risicoanalyses",
    ]
  },
  create: {
    label: "Creëren",
    icon: Lightbulb,
    items: [
      "Bedenk creatieve ideeën",
      "Ontwerp een nieuw productconcept",
      "Maak een marketingcampagne",
      "Zet een opzet voor een presentatie op",
      "Schrijf berichten voor sociale media",
    ]
  },
  write: {
    label: "Schrijven",
    icon: PenTool,
    items: [
      "Schrijf een zakelijke e-mail",
      "Schrijf een blogbericht",
      "Maak documentatie",
      "Schrijf een motivatiebrief",
      "Schrijf een toespraak",
    ]
  },
  learn: {
    label: "Leren",
    icon: GraduationCap,
    items: [
      "Leg een ingewikkeld begrip uit",
      "Vat een onderwerp samen",
      "Maak een studiegids",
      "Vergelijk verschillende aanpakken",
      "Geef leerbronnen",
    ]
  },
  code: {
    label: "Code",
    icon: Code,
    items: [
      "Zoek de fout in deze code",
      "Leg deze functie uit",
      "Schrijf unittests",
      "Herschrijf voor betere prestaties",
      "Maak een API-endpoint",
    ]
  }
};

const MAX_FILE_BYTES = 10 * 1024 * 1024;
// Read-as-text extensions — merged directly into the outgoing message as a
// fenced code block rather than sent as a binary attachment. Images and
// PDF/DOCX documents go through as real attachments (see DOCUMENT_EXTENSIONS
// below); the backend extracts document text server-side.
const TEXT_EXTENSIONS = new Set([
  "txt", "md", "csv", "json", "yaml", "yml", "toml", "ini", "cfg", "log", "xml",
  "py", "js", "jsx", "ts", "tsx", "css", "html", "sh", "bat", "ps1", "sql",
  "rb", "go", "rs", "java", "c", "cpp", "h", "hpp", "cs", "php", "swift", "kt",
]);

function fileExt(filename) {
  const parts = filename.split(".");
  return parts.length > 1 ? parts.pop().toLowerCase() : "";
}

function formatFileSize(bytes) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

// Slash-command menu — shared by the welcome-screen input and the active-conversation input.
function useSlashMenu(input, setInput, rootCommands, taal = "nl", vertaal = (s) => s) {
  const [open, setOpen] = useState(false);
  const [level, setLevel] = useState("root"); // "root" or a command id that has a submenu
  const [activeIndex, setActiveIndex] = useState(0);

  const commands = level === "root" ? rootCommands : (rootCommands.find(c => c.id === level)?.submenu || []);
  const query = input.startsWith("/") ? input.slice(1) : "";
  // Slash-opdrachten (24 okt): op het hoogste niveau filtert de catalogus, op beide namen en de
  // uitleg; in een submenu (model, raad) blijft het filteren op het label.
  const visible = level === "root"
    ? passend(query, taal, vertaal).map(o => commands.find(c => c.id === o.id)).filter(Boolean)
    : commands.filter(c => c.label.toLowerCase().includes(query.toLowerCase()));

  const onChange = (value) => {
    if (value.startsWith("/") && !value.slice(1).includes(" ")) {
      setOpen(true);
    } else {
      setOpen(false);
      setLevel("root");
    }
    setActiveIndex(0);
  };

  const select = (cmd) => {
    if (!cmd) return;
    if (cmd.submenu) {
      setLevel(cmd.id);
      setInput("/");
      setActiveIndex(0);
      return;
    }
    cmd.run();
    setInput("");
    setOpen(false);
    setLevel("root");
  };

  // Returns true if the key was consumed by the menu (caller should not also handle it).
  const onKeyDown = (e) => {
    if (!open || visible.length === 0) return false;
    if (e.key === "ArrowDown") { e.preventDefault(); setActiveIndex(i => (i + 1) % visible.length); return true; }
    if (e.key === "ArrowUp") { e.preventDefault(); setActiveIndex(i => (i - 1 + visible.length) % visible.length); return true; }
    if (e.key === "Enter") { e.preventDefault(); select(visible[activeIndex]); return true; }
    if (e.key === "Escape") { e.preventDefault(); setOpen(false); setLevel("root"); return true; }
    return false;
  };

  return { open, visible, activeIndex, setActiveIndex, onChange, onKeyDown, select };
}

const DOCUMENT_EXTENSIONS = new Set(["pdf", "docx", "doc", "xlsx", "pptx"]);

// Claude-style attachment chip: image thumbnail, or a file-icon card with
// name + size for anything else (documents, text/code files).
function AttachmentChip({ att, onRemove, compact }) {
  const thumbSize = compact ? "h-12 w-12" : "h-16 w-16";
  if (att.kind === "image") {
    return (
      <div className="relative group">
        <img src={att.preview} alt={att.name} className={cn(thumbSize, "object-cover rounded-lg border")} />
        <button type="button" onClick={onRemove} className="absolute -top-2 -right-2 bg-destructive text-destructive-foreground rounded-full p-0.5 opacity-100 md:opacity-0 md:group-hover:opacity-100 transition-opacity">
          <X className="h-3 w-3" />
        </button>
      </div>
    );
  }
  return (
    <div className="relative group flex items-center gap-2 border rounded-lg pl-2 pr-6 py-1.5 bg-background max-w-[200px]">
      <FileText className="h-5 w-5 text-muted-foreground flex-shrink-0" />
      <div className="min-w-0">
        <p className="text-xs font-medium truncate">{att.name}</p>
        <p className="text-[10px] text-muted-foreground">{formatFileSize(att.size || 0)}</p>
      </div>
      <button type="button" onClick={onRemove} className="absolute -top-2 -right-2 bg-destructive text-destructive-foreground rounded-full p-0.5 opacity-100 md:opacity-0 md:group-hover:opacity-100 transition-opacity">
        <X className="h-3 w-3" />
      </button>
    </div>
  );
}

function SlashMenu({ commands, activeIndex, onHover, onSelect }) {
  const { t } = useTaal();
  return (
    <div className="absolute bottom-full left-0 right-0 mb-2 bg-popover border rounded-xl shadow-lg overflow-hidden z-20 max-h-72 overflow-y-auto"
         data-testid="slash-menu">
      {commands.length === 0 ? (
        <div className="px-3 py-2 text-sm text-muted-foreground">{t("Geen passende opdrachten")}</div>
      ) : commands.map((c, i) => (
        <div key={c.id}>
          {/* Slash-opdrachten (24 okt): een kop zodra de groep wisselt. */}
          {c.groep && commands[i - 1]?.groep !== c.groep && (
            <div className="px-3 pt-2 pb-1 text-[11px] uppercase tracking-wide text-muted-foreground" data-testid="slash-groep">
              {t(c.groep)}
            </div>
          )}
          <button
            type="button"
            onMouseEnter={() => onHover(i)}
            onClick={() => onSelect(c)}
            data-testid={`slash-${c.id}`}
            className={cn(
              "w-full text-left px-3 py-2 text-sm flex flex-col transition-colors",
              i === activeIndex ? "bg-[hsl(var(--accent))]/10" : "hover:bg-muted/50"
            )}
          >
            <span className="font-medium">{c.label}</span>
            {c.hint && <span className="text-xs text-muted-foreground break-words">{c.hint}</span>}
            {c.voorbeeld && <span className="text-[11px] text-muted-foreground/80 font-mono">{c.voorbeeld}</span>}
          </button>
        </div>
      ))}
    </div>
  );
}

/** /help: alle opdrachten per groep, met uitleg en voorbeeld, in de taal van de app. */
export function SlashHelp({ open, onOpenChange }) {
  const { t, taal } = useTaal();
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[640px] max-h-[85vh] overflow-y-auto" data-testid="slash-help">
        <DialogHeader><DialogTitle>{t("Opdrachten")}</DialogTitle></DialogHeader>
        <p className="text-xs text-muted-foreground">
          {t("Typ / in het invoerveld voor de lijst; verder typen filtert, pijltjes en Enter kiezen.")}
        </p>
        {perGroep().map(({ groep, opdrachten }) => (
          <section key={groep} className="mt-3">
            <h3 className="text-sm font-medium">{t(groep)}</h3>
            <ul className="mt-1 space-y-1">
              {opdrachten.map((o) => (
                <li key={o.id} className="text-sm" data-testid={`help-${o.id}`}>
                  <span className="font-mono">/{opdrachtNaam(o, taal)}</span>
                  <span className="text-muted-foreground"> {t(o.uitleg)}</span>
                  <span className="block text-[11px] text-muted-foreground/80 font-mono">{opdrachtVoorbeeld(o, taal)}</span>
                </li>
              ))}
            </ul>
          </section>
        ))}
      </DialogContent>
    </Dialog>
  );
}

/** De tekst van het vaste teken bovenin. PUUR, punt 3 (11 oktober 2026). */
export function openTeken(web, vault) {
  // KORT EN OP EEN REGEL: op 390 px liep "Open: web en vault schrijven" over
  // drie regels en over de rand (gemeten op de kopie, 11 oktober 2026). De
  // volledige uitleg staat in de `title`.
  const delen = [web && "web", vault && "vault"].filter(Boolean);
  return {
    tekst: `Open: ${delen.join(", ")}`,
    uitleg: [web && "Novamund mag een webpagina voor je ophalen.",
             vault && "Novamund mag in je vault schrijven."].filter(Boolean).join(" ")
      + " Klik om dit in Instellingen te wijzigen.",
  };
}

export default function ChatArea({
  messages,
  isLoading,
  isStreaming,
  onSendMessage,
  onStop,
  onToggleSidebar,
  isSidebarOpen,
  hasApiKey,
  webAccess = false,
  vaultWrite = false,
  onOpenSettings,
  onOpenSearch,
  onOpenTimeMachine,
  currentModel,
  currentProvider,
  onModelChange,
  orchestrateMode,
  onToggleOrchestrate,
  councilMode,
  onSetCouncilMode,
  councilStatus,
  onNewChat,
  projects = [],
  projectId = null,
  onOpenProject,
  onMoveToProject,
  proposal = null,
  onRememberProposal,
  onDismissProposal,
  notice = null,
  archiveBases = [],
  archiveOn = [],
  onToggleArchive,
  onAllArchiveOff,
  onAllArchiveOn,
  archiveSoort = "project",
  // MK2 (3 nov): de modelkeuze van dit gesprek, de standaard uit Instellingen, en kiezen.
  modelKeuze = null,
  standaardModel = null,
  onKiesModel,
  onKiesNiveau,
  // MK6 (3 nov): geheugen en onderzoek op het web in dit gesprek, en ze zetten.
  plusOpties = null,
  onZetOptie,
  // I7: het plusmenu (connectors per gesprek, beheren, skills).
  connectors = [],
  connectorRol = "",
  onToggleConnector,
  onOpenSkills,
  // PUNT 4b, 11 oktober 2026: een regel die ONDER de kop hoort, zoals "er wacht
  // iets in Geheugen". Boven ChatArea viel hij op de iPhone over de klok en de
  // batterij, want alleen deze kop houdt rekening met de veilige zone.
  bovenbalk = null,
  // PUNT 4a, 11 oktober 2026: de PWA op de iPhone heeft geen verversknop van de
  // browser. Een prop zodat een toets kan zien dat er ververst wordt.
  onVerversen = () => window.location.reload(),
  // W1v1: de kaarten van koppelaanroepen die in DIT gesprek op een klik wachten.
  gesprekId = null,
  onKoppelingBeslist,
}) {
  const { t, taal } = useTaal();
  const navigateSlash = useNavigate();
  const [input, setInput] = useState("");
  // Post U: het Universum, geopend met de knop naast de klok van de Time Machine.
  const [universumOpen, setUniversumOpen] = useState(false);
  // Slash-opdrachten (24 okt): /help en /koppelingen openen hier een venster.
  const [helpOpen, setHelpOpen] = useState(false);
  const [koppelingenOpen, setKoppelingenOpen] = useState(false);
  // Deel 2: /worker en /agent zonder tekst openen een formulier ("worker", "agent" of null).
  const [workerFormulier, setWorkerFormulier] = useState(null);
  const [attachments, setAttachments] = useState([]);
  const [isDragging, setIsDragging] = useState(false);
  const textareaRef = useRef(null);
  const fileInputRef = useRef(null);
  const messagesEndRef = useRef(null);
  const viewportRef = useRef(null);
  // Waar de lezer stond VOORDAT er iets bij kwam. Een ref en geen state, om
  // twee redenen: hij mag niets hertekenen, en hij moet tijdens het effect
  // nog de OUDE waarde dragen. Dat klopt vanzelf, want tekst erbij zetten
  // maakt de afstand tot onderen groter zonder een scroll-gebeurtenis te
  // veroorzaken; de meting hieronder loopt dus altijd een stap achter, en
  // precies die stap is wat we willen weten.
  const stondOnderaanRef = useRef(true);
  const aantalBerichtenRef = useRef(0);

  const providerModels = PROVIDERS[currentProvider]?.models || [];

  // A council of one is not a council. The server knows how many models are
  // available; until it has said so (councilStatus null) the button stays
  // usable, because "not asked yet" is not the same as "unavailable".
  const councilMembers = councilStatus?.members || [];
  const councilUnavailable = councilStatus ? !councilStatus.ready : false;
  const councilReason = councilMembers.length === 1
    ? `The council needs at least two models. Only ${councilMembers[0].provider} (${councilMembers[0].model}) is available. Add another provider's API key in Settings.`
    : t("De raad heeft minstens twee modellen nodig. Voeg in Instellingen een API-sleutel van een aanbieder toe, of start Ollama.");
  const councilTitle = councilUnavailable
    ? councilReason
    : councilMode ? `LLM Council ON (${councilMode})` : t("LLM-raad UIT");

  // SL-a: een formulier openen, op zijn eigen pagina of in de zijbalk.
  const openFormulier = (id, naam = "") => {
    const doel = formulierVan(id, naam);
    if (!doel) return;
    if (doel.gebeurtenis) window.dispatchEvent(new CustomEvent(doel.gebeurtenis, { detail: { naam: doel.naam } }));
    else navigateSlash(doel.pad, { state: { nieuw: doel.naam } });
  };

  // Slash-opdrachten (24 okt 2026): wat elke opdracht uit `lib/slashopdrachten.js` DOET. Het
  // label is de naam in de taal van de app, de hint de uitleg; de zes van voor vandaag blijven.
  const doen = {
    nieuw: { run: () => onNewChat?.() },
    model: {
      submenu: providerModels.length > 0
        ? providerModels.map(m => ({ id: m.value, label: m.label, run: () => onModelChange?.(m.value) }))
        : [{ id: "none", label: currentProvider === "ollama" ? t("Haal een model op in Instellingen") : t("Stel een aanbieder in onder Instellingen"), run: () => onOpenSettings?.() }],
    },
    instellingen: { run: () => onOpenSettings?.() },
    chats: { run: () => onOpenSearch?.() },
    orkestratie: { run: () => onToggleOrchestrate?.() },
    raad: {
      submenu: councilUnavailable ? [
        { id: "unavailable", label: t("Niet beschikbaar"), run: () => onOpenSettings?.() },
      ] : [
        { id: "off", label: "Off", run: () => onSetCouncilMode?.(null) },
        { id: "synthesis", label: t("Synthese"), run: () => onSetCouncilMode?.("synthesis") },
        { id: "consensus", label: t("Consensus (beoordeeld)"), run: () => onSetCouncilMode?.("consensus") },
        { id: "best_of_n", label: t("Beste van N"), run: () => onSetCouncilMode?.("best_of_n") },
      ],
    },
    universum: { run: () => setUniversumOpen(true) },
    logboek: { run: () => onOpenSettings?.("advanced") },
    postbus: { run: () => navigateSlash("/postbus") },
    geheugen: { run: () => navigateSlash("/memory") },
    koppelingen: { run: () => setKoppelingenOpen(true) },
    skills: { run: () => navigateSlash("/skills") },
    agenten: { run: () => navigateSlash("/agents") },
    tijdmachine: { run: () => onOpenTimeMachine?.() },
    help: { run: () => setHelpOpen(true) },
    // SL-a: het bestaande formulier, leeg; met een naam erachter gaat het via handleSubmit.
    project: { run: () => openFormulier("project") },
    kennisbank: { run: () => openFormulier("kennisbank") },
    team: { run: () => openFormulier("team") },
    skill: { run: () => openFormulier("skill") },
    worker: { run: () => setWorkerFormulier("worker") },
    agent: { run: () => setWorkerFormulier("agent") },
    // Deel 3: een leesopdracht zonder tekst stelt meteen de vraag.
    taken: { run: () => onSendMessage(zinZonderTekst("taken", taal), []) },
    mail: { run: () => onSendMessage(zinZonderTekst("mail", taal), []) },
    agenda: { run: () => onSendMessage(zinZonderTekst("agenda", taal), []) },
    meetings: { run: () => onSendMessage(zinZonderTekst("meetings", taal), []) },
    drive: { run: () => onSendMessage(zinZonderTekst("drive", taal), []) },
  };
  const rootCommands = OPDRACHTEN.filter(o => doen[o.id]).map(o => ({
    id: o.id, groep: o.groep, label: `/${opdrachtNaam(o, taal)}`, voorbeeld: t("Voorbeeld: {v}", { v: opdrachtVoorbeeld(o, taal) }),
    hint: o.id === "model" ? `${t(o.uitleg)} (${currentModel || "none"})` : t(o.uitleg),
    ...doen[o.id],
  }));
  const slash = useSlashMenu(input, setInput, rootCommands, taal, t);
  // AT, @-agents (25 okt 2026): het menu achter @, naast het slash-menu.
  const at = useAgentMenu(input, setInput);

  // Bijhouden waar de lezer staat. Passief, want dit luistert alleen mee en
  // houdt het scrollen nooit tegen.
  useEffect(() => {
    const viewport = viewportRef.current;
    if (!viewport) return undefined;
    const meet = () => { stondOnderaanRef.current = staatOnderaan(viewport); };
    meet();
    viewport.addEventListener("scroll", meet, { passive: true });
    return () => viewport.removeEventListener("scroll", meet);
  }, []);

  // Meescrollen, maar alleen als dat mag. Zie `lib/meescrollen.js` voor de
  // regel zelf en voor het gebrek dat hij repareert.
  useEffect(() => {
    const nieuwBericht = messages.length > aantalBerichtenRef.current;
    aantalBerichtenRef.current = messages.length;
    if (!magMeescrollen({ nieuwBericht, stondOnderaan: stondOnderaanRef.current })) return;
    messagesEndRef.current?.scrollIntoView({ behavior: gedragVoor(isStreaming) });
    // We zetten hem zojuist zelf onderaan, dus dat is vanaf nu de stand. Zonder
    // deze regel hangt het van de timing van de scroll-gebeurtenis af of het
    // volgende stukje tekst nog meescrolt, en dan stokt het halverwege.
    stondOnderaanRef.current = true;
  }, [messages, isStreaming]);

  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = "auto";
      textareaRef.current.style.height = Math.min(textareaRef.current.scrollHeight, 200) + "px";
    }
  }, [input]);

  const processFiles = (fileList) => {
    const files = Array.from(fileList || []);
    for (const file of files) {
      if (file.size > MAX_FILE_BYTES) {
        toast.error(t("{f} is te groot (hooguit 10 MB)", { f: file.name }));
        continue;
      }
      // Glasronde 0.9.166, fout 4: een online-only bestand uit Dropbox of iCloud
      // komt als 0 bytes binnen (gemeten: `com.dropbox.placeholder`). Niet bijvoegen,
      // want anders gaat er een lege bijlage mee die het model niet kan lezen.
      if (file.size === 0) {
        toast.error(t("{f}: het bestand is leeg of nog niet gedownload. Download het eerst en voeg het dan opnieuw toe.", { f: file.name }));
        continue;
      }
      const ext = fileExt(file.name);
      if (file.type.startsWith("image/")) {
        const reader = new FileReader();
        reader.onload = (event) => {
          setAttachments(prev => [...prev, {
            kind: "image", name: file.name, type: file.type, size: file.size,
            data: event.target.result.split(",")[1], preview: event.target.result,
          }]);
        };
        reader.readAsDataURL(file);
      } else if (DOCUMENT_EXTENSIONS.has(ext)) {
        const reader = new FileReader();
        reader.onload = (event) => {
          setAttachments(prev => [...prev, {
            kind: "document", name: file.name, size: file.size,
            type: file.type || "application/octet-stream",
            data: event.target.result.split(",")[1],
          }]);
        };
        reader.readAsDataURL(file);
      } else if (TEXT_EXTENSIONS.has(ext) || file.type.startsWith("text/")) {
        const reader = new FileReader();
        reader.onload = (event) => {
          setAttachments(prev => [...prev, {
            kind: "text", name: file.name, size: file.size, content: event.target.result,
          }]);
        };
        reader.readAsText(file);
      } else {
        toast.error(t("{f}: dit soort bestand wordt niet ondersteund", { f: file.name }));
      }
    }
  };

  const handleFileSelect = (e) => {
    processFiles(e.target.files);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => setIsDragging(false);

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    processFiles(e.dataTransfer.files);
  };

  const handleSuggestionClick = (suggestion) => {
    setInput(suggestion);
    textareaRef.current?.focus();
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if ((input.trim() || attachments.length > 0) && !isStreaming) {
      const textFiles = attachments.filter(a => a.kind === "text");
      const fileAttachments = attachments.filter(a => a.kind !== "text");
      let content = input.trim();
      // Slash-opdrachten deel 2: "/worker <tekst>" wordt de zin die de workerpoort kent, en dan
      // volgt de bestaande voorstelkaart; er ontstaat niets zonder Bevestig.
      const opdracht = metTekst(content);
      // SL-a: "/project Chalet Check" verstuurt niets; het formulier opent met de naam erin.
      if (opdracht?.opdracht.formulier) {
        openFormulier(opdracht.opdracht.id, opdracht.tekst);
        setInput("");
        return;
      }
      if (opdracht) content = zinMetTekst(opdracht.opdracht.id, opdracht.tekst, taal);
      if (textFiles.length > 0) {
        const blocks = textFiles.map(t => `--- ${t.name} ---\n\`\`\`\n${t.content}\n\`\`\``).join("\n\n");
        content = content ? `${content}\n\n${blocks}` : blocks;
      }
      onSendMessage(content, fileAttachments.map(a => ({ type: a.type, data: a.data, name: a.name })));
      setInput("");
      setAttachments([]);
    }
  };

  const handleKeyDown = (e) => {
    if (slash.onKeyDown(e)) return;
    if (at.onKeyDown(e)) return;
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  return (
    /* `min-h-0` is hier GEEN opsmuk maar de reden dat het gesprek scrolt.
     *
     * **HET GEBREK, gemeten op Franks scherm op 24 augustus 2026.** In een
     * venster van 957 bij 949 was dit element **1409 pixels hoog**. Een flex-item
     * in een kolom krijgt standaard `min-height: auto`, en dat betekent letterlijk:
     * krimp niet onder je eigen inhoud. Dus groeide deze `main` mee met het
     * gesprek, kreeg de ScrollArea eronder de volle inhoudshoogte, en had de
     * viewport niets meer om te scrollen. Het invoerveld zakte onder de vouw en
     * de rest werd weggeknipt door de `overflow-hidden` van de schil.
     *
     * Gemeten in een echte browser op een schil van 900 px, met tien berichten:
     *
     *   zonder min-h-0 : main 39193, viewport 39064 zichtbaar van 39064 -> scrolt NIET
     *   met    min-h-0 : main   783, viewport   654 zichtbaar van 39064 -> scrolt WEL
     *
     * **DIT IS NOOIT DOOR EEN TOETS GEZIEN EN DAT KON OOK NIET.** jsdom rekent
     * geen opmaak uit: daar is elke hoogte nul, dus alle 1088 frontend-toetsen
     * stonden groen boven een scherm dat niet scrolde. Zie
     * `meting/opmaak.mjs` voor de meting die dit wel kan zien.
     *
     * `KnowledgePage` en `AgentsPage` hebben dezelfde klassenreeks en gaan wel
     * goed, omdat daar `overflow-y-auto` op de `main` staat; een eigen overflow
     * zet diezelfde automatische ondergrens op nul. Deze was de enige zonder.
     */
    <main className="flex-1 flex flex-col min-h-0 min-w-0 relative bg-[hsl(var(--background))]" data-testid="chat-area">
      {/* Header - minimal. viewport-fit=cover means the page reaches under the
          status bar and the Dynamic Island, so without this inset the only
          button that opens the sidebar sits beside the clock, where iOS
          swallows the tap. Reported from an iPhone on 2026-07-26. */}
      <header className="flex flex-col px-4 pt-[var(--veilig-boven)]" data-testid="chat-header">
        {/* The header used to hold one button and nothing else. Which project
            you are in was knowable from the sidebar only, and on a phone the
            sidebar is a drawer that is closed. */}
        <div className="h-12 flex items-center gap-2">
          {!isSidebarOpen && (
            <Button variant="ghost" size="icon" onClick={onToggleSidebar} className="tap-target h-8 w-8" data-testid="mobile-sidebar-toggle">
              <PanelLeft className="h-4 w-4" />
            </Button>
          )}
          <Button variant="ghost" size="icon" onClick={() => onVerversen()}
            className="tap-target h-8 w-8 md:hidden" aria-label={t("kop.verversen")} title={t("kop.verversen")}
            data-testid="verversen">
            <RotateCw className="h-4 w-4" />
          </Button>
          <ProjectBadge
            projects={projects}
            projectId={projectId}
            onOpenProject={onOpenProject}
            onMoveToProject={onMoveToProject}
          />
          {/* DE TIJDMACHINE, BEREIKBAAR TIJDENS EEN GESPREK.
              Franks eis van 24 augustus 2026: het klokje op het welkomstscherm
              is alleen te bereiken bij een LEEG gesprek, en dat is precies niet
              het moment waarop je wilt terugkijken. Deze staat in de kop en is
              er dus altijd.

              `ml-auto` duwt hem naar rechts; het webtoegangslabel hierna doet
              dat ook, en `ml-auto` op twee elementen laat alleen het EERSTE
              opschuiven. Daarom staat deze knop hier en niet erna: dan houden ze
              elkaar rechts vast in plaats van dat er een in het midden hangt. */}
          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={() => onOpenTimeMachine?.()}
            className="tap-target h-8 w-8 ml-auto flex-shrink-0"
            title={t("kop.tijdmachine")}
            aria-label="Time machine"
            data-testid="time-machine-btn"
          >
            <Clock className="h-4 w-4" />
          </Button>
          {/* POST U (Frank 23 oktober 2026): het Universum, naast de klok. */}
          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={() => setUniversumOpen(true)}
            className="tap-target h-8 w-8 flex-shrink-0"
            title={t("kop.universum")}
            aria-label={t("Universum")}
            data-testid="universum-btn"
          >
            <Orbit className="h-4 w-4" />
          </Button>
          <UniversumVenster open={universumOpen} onOpenChange={setUniversumOpen} />
          <SlashHelp open={helpOpen} onOpenChange={setHelpOpen} />
          <SlashWorkerFormulier soort={workerFormulier} onOpenChange={setWorkerFormulier}
            onStuur={(zin) => onSendMessage(zin, [])} />
          <KoppelingenVenster open={koppelingenOpen} onOpenChange={setKoppelingenOpen} />

          {/* Only when it is on. A label that is always there stops being
              read, and the thing worth knowing is that this assistant can
              go out to the internet right now. */}
          {/* PUNT 3, 11 oktober 2026: DIT IS DE ENIGE PLEK waar een open grens
              staat. De regel boven elk antwoord is weg; Frank wil alleen een
              klein vast teken bovenin. Zie `openTeken` voor de tekst. */}
          {(webAccess || vaultWrite) && (
            <button
              type="button"
              onClick={onOpenSettings}
              title={openTeken(webAccess, vaultWrite).uitleg}
              className="ml-auto flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full border border-primary/25 bg-primary/10 px-2.5 py-1 text-[11px] text-muted-foreground tap-target"
              data-testid="open-grenzen"
            >
              {/* Geen wereldbol op een telefoon: op 390 px stond het teken 5 px
                  over de rand (gemeten op de kopie). */}
              <Globe className="hidden sm:block h-3 w-3 flex-shrink-0" />
              {openTeken(webAccess, vaultWrite).tekst}
            </button>
          )}
        </div>
      </header>
      {bovenbalk}

      {/* Messages Area */}
      {/* `[&>div>div]:!block` is de reden dat het gesprek op een telefoon binnen
          het scherm blijft.

          **HET GEBREK, gemeld door Frank op 24 augustus 2026 vanaf een iPhone in
          staande stand.** Niet alleen een tabel maar OOK gewone tekst liep rechts
          buiten beeld, en een genummerde lijst werd afgekapt. Horizontaal draaien
          hielp, en dat hoort niet nodig te zijn.

          **DE OORZAAK.** Radix rendert het kind van zijn scroll-viewport als
          `display: table` met `min-width: 100%`, en een tabel KRIMPT NAAR ZIJN
          INHOUD in plaats van zijn container te gehoorzamen. Gemeten met een schil
          van 390: die div werd 768 breed in een viewport van 384, en de hele
          berichtkolom erfde dat.

          Gemeten, met en zonder deze klasse, op een schil van 390:

            zonder : inhoudsdiv 768 (table), tekst tot 752, tabelvenster 736
            met    : inhoudsdiv 384 (block), tekst tot 368, tabelvenster 352

          **DE TABEL SCHOOF AL WEL, en dat is de subtiele helft.** Zijn
          schuifvenster werkte, maar was zelf 736 breed in een viewport van 384,
          dus de helft ervan stond buiten het scherm. Je kunt niet schuiven aan
          iets wat je niet kunt aanraken.

          **DEZE REPARATIE STOND SINDS 26 JULI 2026 AL IN `Sidebar.jsx`**, met de
          toelichting erbij ("measured at 398 points inside a 279 point scroller").
          Dit scherm had haar niet. Dezelfde fout, dezelfde component, een maand
          later, een scherm verderop.

          **EN OP EEN BREED SCHERM IS HIJ ONZICHTBAAR:** daar begrenst `max-w-3xl`
          de kolom op 768 en komt de krimp op datzelfde getal uit. Daarom draait
          `meting/opmaak.mjs` sinds vandaag op TWEE breedtes. */}
      <ScrollArea className="flex-1 [&>div>div]:!block" viewportRef={viewportRef}>
        <div className="max-w-3xl mx-auto px-4 py-8">
          {/* GL201-2: een verlopen Google-toegang al bij het openen, niet pas na een poging. */}
          <VerlopenMelding />
          {messages.length === 0 ? (
            <WelcomeScreen 
              hasApiKey={hasApiKey} 
              onOpenSettings={onOpenSettings}
              onSuggestionClick={handleSuggestionClick}
              input={input}
              setInput={setInput}
              onSubmit={handleSubmit}
              isStreaming={isStreaming}
              attachments={attachments}
              setAttachments={setAttachments}
              fileInputRef={fileInputRef}
              handleFileSelect={handleFileSelect}
              textareaRef={textareaRef}
              handleKeyDown={handleKeyDown}
              slash={slash}
              at={at}
              isDragging={isDragging}
              handleDragOver={handleDragOver}
              handleDragLeave={handleDragLeave}
              handleDrop={handleDrop}
              councilMode={councilMode}
              onSetCouncilMode={onSetCouncilMode}
              councilUnavailable={councilUnavailable}
              councilTitle={councilTitle}
              archiveBases={archiveBases}
              archiveOn={archiveOn}
              onToggleArchive={onToggleArchive}
              onAllArchiveOff={onAllArchiveOff}
              onAllArchiveOn={onAllArchiveOn}
              archiveSoort={archiveSoort}
              connectors={connectors}
              connectorRol={connectorRol}
              onToggleConnector={onToggleConnector}
              onOpenSkills={onOpenSkills}
              modelKeuze={modelKeuze}
              plusOpties={plusOpties}
              onZetOptie={onZetOptie}
              webAccess={webAccess}
              standaardModel={standaardModel}
              onKiesModel={onKiesModel}
              onKiesNiveau={onKiesNiveau}
            />
          ) : (
            <div className="space-y-6">
              {messages.map((message, index) => (
                /* GL205-1 (glasronde 0.9.205, Frank 25 oktober 2026): alles onder een antwoord volgt
                   de taal van de app. Hier stond een `BlokTaal` die het blok in de taal van het
                   antwoord tekende (besluit 15 oktober), en dan stonden er met de app op EN onder een
                   Nederlandse vraag "Nagedacht", "bron" en "Maak document". De antwoordtekst zelf
                   blijft in de taal van de vraag; `data-taal` draagt dat besluit nog voor wie het wil zien. */
                <div key={message.id} className="space-y-3" data-taal={message.taal || undefined}>
                  <MessageBubble
                    message={message}
                    isStreaming={isStreaming && index === messages.length - 1 && message.role === "assistant"}
                    /* POST 10B-GLAS. De voorstelkaart heeft twee wegen terug
                       naar dit scherm nodig: "Wijzig" zet de velden als zin in
                       het invoerveld, en een klik op een werkruimte stuurt de
                       zin opnieuw met die naam erbij.

                       ZE GAAN ALS PROP EN NIET VIA EEN EVENT OP `window`,
                       zodat de kaart te toetsen is zonder een halve
                       browseromgeving en zodat er geen tweede weg ontstaat
                       waarlangs iets een bericht kan versturen. */
                    onZetInInvoer={handleSuggestionClick}
                    onStuur={(zin) => onSendMessage(zin, [])}
                  />
                  {/* Under the answer it belongs to, so it reads as something
                      noticed in this exchange rather than a standing prompt.

                      Matched by message id from state the page holds, not from
                      a field on the message. The message list is refetched
                      from the server the moment an answer finishes, and the
                      server does not return the proposal: anything hung on the
                      message object is wiped a second after it appears. */}
                  {proposal?.messageId === message.id && (
                    <MemoryProposal
                      text={proposal.text}
                      onRemember={onRememberProposal}
                      onDismiss={onDismissProposal}
                    />
                  )}
                  {/* Held the same way and for the same reason. This one is
                      not a question: it says what happened to something he
                      asked to be remembered, including when the answer is
                      that nothing was. */}
                  {notice?.messageId === message.id && <MemoryNotice notice={notice} />}
                </div>
              ))}
              <div ref={messagesEndRef} />
            </div>
          )}
        </div>
      </ScrollArea>

      {/* Input Area - only show when there are messages */}
      {messages.length > 0 && (
        <>
          {attachments.length > 0 && (
            <div className="border-t bg-background/80 px-4 py-2">
              <div className="max-w-3xl mx-auto flex gap-2 flex-wrap">
                {attachments.map((att, i) => (
                  <AttachmentChip key={i} att={att} onRemove={() => setAttachments(prev => prev.filter((_, idx) => idx !== i))} />
                ))}
              </div>
            </div>
          )}
          {/* index.html asks for viewport-fit=cover, so the app reaches under
              the home indicator. Without this padding the send button sits
              behind it and the swipe-up gesture wins every tap. */}
          <div className="border-t bg-background/80 backdrop-blur-sm pb-[env(safe-area-inset-bottom)]" data-testid="composer">
            {/* W1v1: een bestellink of aanvraag uit dit gesprek wacht hier op je klik,
                boven het invoerveld, met dezelfde kaart als bij Agenten. */}
            {gesprekId && (
              <div className="max-w-3xl mx-auto">
                <McpBevestigingen gesprekId={gesprekId} ververs={`${messages.length}-${isStreaming}`}
                                  onBeslist={onKoppelingBeslist} />
              </div>
            )}
            {/* HIER STOND EEN BALK MET EEN PIL PER REGEL, en die is op Franks
                opdracht van 23 augustus 2026 vervangen door een knop in de rij
                hieronder. Met vier `default_off`-banken viel die balk niet op;
                sinds R47 stap 2 zijn het 18 projecten, en dan duwt hij met acht
                regels het hele scherm weg. */}
            <form onSubmit={handleSubmit} className="max-w-3xl mx-auto p-4">
              {/* HET VELD OP EEN EIGEN REGEL EN DE KNOPPEN ERONDER.
                  FRANKS MELDING VAN 23 AUGUSTUS 2026, op een iPhone: "een zin
                  van vijf woorden breekt in drie regels".

                  DE REKENSOM DIE DAT VERKLAART, op 390 pixels breed. Deze rij
                  droeg VIJF knoppen van 36 pixels naast het veld, plus vijf
                  tussenruimtes van 8 en de vulling van de pil en het formulier:
                  36*5 + 8*5 + 16 + 32 is 268 pixels. Van de 390 bleef er dan
                  ongeveer 120 over voor de tekst, en dat is drie a vier woorden
                  per regel.

                  DIT IS DEZELFDE VORM ALS HET WELKOMSTSCHERM en dat is met
                  opzet: daar stond het veld al op een eigen regel met de
                  knoppen eronder, en dat scherm heeft dit gebrek nooit gehad.
                  Twee invoerbalken die er anders uitzien zijn twee plekken waar
                  dit opnieuw kan misgaan. */}
              <div
                className={cn(
                  "relative flex flex-col gap-2 bg-[hsl(var(--muted))] rounded-2xl p-2 transition-shadow",
                  isDragging && "ring-2 ring-[hsl(var(--accent))]"
                )}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
              >
                {slash.open && (
                  <SlashMenu commands={slash.visible} activeIndex={slash.activeIndex} onHover={slash.setActiveIndex} onSelect={slash.select} />
                )}
                <AgentMenu menu={at} />
                <input ref={fileInputRef} type="file" accept="image/*,.pdf,.docx,.doc,.xlsx,.pptx,.txt,.md,.csv,.json,.yaml,.yml,.py,.js,.jsx,.ts,.tsx,.css,.html,.sh,.sql,.log" multiple onChange={handleFileSelect} className="hidden" />
                <Textarea
                  ref={textareaRef}
                  value={input}
                  onChange={(e) => { setInput(e.target.value); slash.onChange(e.target.value); at.onChange(); }}
                  onKeyDown={handleKeyDown}
                  placeholder={t("kop.opdrachten")}
                  disabled={isStreaming || !hasApiKey}
                  // VIJF REGELS EN DAARNA SCROLLEN. 136 pixels is 5 * 24 voor de
                  // regels plus 16 voor de vulling; 24 is `text-base` met de
                  // regelhoogte die deze component al had. Er stond 200, en dat
                  // is ruim acht regels: dan duwt een geplakte alinea het
                  // gesprek van het scherm.
                  //
                  // `text-base` STAAT ER EXPLICIET BIJ, ook al zet de component
                  // hem al. Onder 16 pixels zoomt iOS bij het focussen in, en
                  // dan staat het scherm scheef zodra je begint te typen. Dat is
                  // een eis en geen opmaak, dus zij hoort zichtbaar te zijn op de
                  // plek waar iemand hem zou weghalen.
                  className="w-full min-h-[40px] max-h-[136px] resize-none border-0 bg-transparent focus-visible:ring-0 p-2 text-base"
                  rows={1}
                  data-testid="message-input"
                />
                <div className="flex items-center gap-1" data-testid="composer-buttons">
                {/* I7: het plusmenu, op beide plekken hetzelfde. Bestanden is de
                    bijlage van voorheen, met dezelfde voorwaarde. */}
                <PlusMenu onFiles={() => fileInputRef.current?.click()} filesDisabled={isStreaming || !hasApiKey}
                          connectors={connectors} rol={connectorRol} onToggleConnector={onToggleConnector}
                          onOpenSkills={onOpenSkills}
                          opties={plusOpties} onZetOptie={onZetOptie} webInInstellingen={webAccess}
                          className="h-9 w-9" />
                {/* MK2 (3 nov): de modelkiezer naast Plus, voor dit gesprek. */}
                <ModelKiezer keuze={modelKeuze} standaard={standaardModel} onKies={onKiesModel} onKiesNiveau={onKiesNiveau}
                             onEigenModel={() => onOpenSettings?.("provider")} className="h-9" />
                {/* IN EEN LOPEND GESPREK IS ER GEEN KLOKJE OM NAAST TE STAAN,
                    dus staat de knop op dezelfde hoek en in dezelfde rol als op
                    het welkomstscherm: linksonder bij de plus. Zelfde component,
                    zelfde gedrag, twee ophangpunten. */}
                <SwitchMenu bases={archiveBases} on={archiveOn} soort={archiveSoort}
                            onToggle={onToggleArchive} onAllOff={onAllArchiveOff} onAllOn={onAllArchiveOn} />
                {/* DE VERZENDKNOP NAAR RECHTS, en de rest blijft links. Zonder
                    deze duwer staan alle vijf op een kluitje links en is de
                    verzendknop niet meer waar je duim hem verwacht. */}
                <div className="flex-1" />
                <Button
                  type="button"
                  variant={orchestrateMode ? "default" : "ghost"}
                  size="icon"
                  onClick={onToggleOrchestrate}
                  disabled={isStreaming || !hasApiKey}
                  className={cn("tap-target h-9 w-9 rounded-xl flex-shrink-0", orchestrateMode ? "bg-corona hover:bg-corona/80 text-[hsl(var(--primary-foreground))]" : "text-muted-foreground")}
                  title={orchestrateMode ? t("Orkestratie met meerdere agenten AAN") : t("Orkestratie met meerdere agenten UIT")}
                >
                  <GitBranch className="h-4 w-4" />
                </Button>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      type="button"
                      variant={councilMode ? "default" : "ghost"}
                      size="icon"
                      disabled={isStreaming || councilUnavailable}
                      className={cn("tap-target h-9 w-9 rounded-xl flex-shrink-0", councilMode ? "bg-nebula hover:bg-nebula/80 text-[hsl(var(--nebula-foreground))]" : "text-muted-foreground")}
                      title={councilTitle}
                      aria-label={councilTitle}
                      data-testid="council-toggle-btn"
                    >
                      <Landmark className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-48">
                    <DropdownMenuItem onClick={() => onSetCouncilMode?.(null)}>
                      {!councilMode && <Check className="h-3 w-3 mr-2" />}<span className={councilMode ? "ml-5" : ""}>{t("Raad uit")}</span>
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => onSetCouncilMode?.("synthesis")}>
                      {councilMode === "synthesis" && <Check className="h-3 w-3 mr-2" />}<span className={councilMode !== "synthesis" ? "ml-5" : ""}>{t("Synthese")}</span>
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => onSetCouncilMode?.("consensus")}>
                      {councilMode === "consensus" && <Check className="h-3 w-3 mr-2" />}<span className={councilMode !== "consensus" ? "ml-5" : ""}>{t("Consensus (beoordeeld)")}</span>
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => onSetCouncilMode?.("best_of_n")}>
                      {councilMode === "best_of_n" && <Check className="h-3 w-3 mr-2" />}<span className={councilMode !== "best_of_n" ? "ml-5" : ""}>{t("Beste van N")}</span>
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
                {/* POST G: tijdens een antwoord wordt de verstuurknop een Stop. */}
                {isStreaming && onStop ? (
                  <Button type="button" size="icon" onClick={onStop} aria-label={t("kop.stop")}
                          className="tap-target h-9 w-9 rounded-xl flex-shrink-0" variant="outline"
                          data-testid="stop-antwoord-btn">
                    <Square className="h-4 w-4" />
                  </Button>
                ) : (
                <Button type="submit" size="icon" disabled={(!input.trim() && attachments.length === 0) || isStreaming || !hasApiKey} className="tap-target h-9 w-9 rounded-xl bg-[hsl(var(--accent))] hover:bg-[hsl(var(--accent))]/90 flex-shrink-0" data-testid="send-message-btn">
                  <Send className="h-4 w-4" />
                </Button>
                )}
                </div>
              </div>
            </form>
          </div>
        </>
      )}
    </main>
  );
}

function WelcomeScreen({ hasApiKey, onOpenSettings, onSuggestionClick, input, setInput, onSubmit, isStreaming, attachments, setAttachments, fileInputRef, handleFileSelect, textareaRef, handleKeyDown, slash, at, isDragging, handleDragOver, handleDragLeave, handleDrop, councilMode, onSetCouncilMode, councilUnavailable, councilTitle, archiveBases = [], archiveOn = [], onToggleArchive, onAllArchiveOff, onAllArchiveOn, archiveSoort = "project", connectors = [], connectorRol = "", onToggleConnector, onOpenSkills, modelKeuze = null, standaardModel = null, onKiesModel, onKiesNiveau, plusOpties = null, onZetOptie, webAccess = false }) {
  const { t } = useTaal();
  const navigate = useNavigate();
  return (
    <div className="stellar-field flex flex-col items-center justify-center min-h-[70vh] px-4 text-center animate-fade-in">
      {/*
        The brand moment. Brand guide sections 04 and 05: the serif carries the
        headline, Stellar Horizon paints the second line, and Supernova appears
        once, small, on the badge. Scarcity is the rule that makes the orange
        mean something, so it is not repeated below.
      */}
      <div
        className="mb-5 inline-flex items-center gap-2 rounded-full border border-[rgba(255,107,53,0.28)] bg-[rgba(255,107,53,0.08)] px-3 py-1"
        data-testid="welcome-badge"
      >
        {/* An SVG mark, not a glyph. The one it replaced was a dingbat that
            rendered as a different shape on every machine. */}
        <Sparkles className="h-3 w-3 shrink-0 text-[hsl(var(--primary))]" aria-hidden="true" />
        <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-[hsl(var(--primary))] sm:text-xs">
          Neural workspace v2.0
        </span>
      </div>

      <div className="mb-6 max-w-3xl">
        <h1 className="font-serif text-4xl font-normal leading-[1.12] tracking-tight sm:text-5xl md:text-6xl">
          {/* Tokens, not the hex from the guide. In dark they already resolve
              to Starlight and Lunar exactly; hard-coding them would have made
              the light theme unreadable, which is a worse kind of wrong than
              being one shade off. */}
          <span className="block text-foreground">How can I help you</span>
          {/* Two spans on purpose: the gradient is clipped to the glyphs, and
              the filter that makes it breathe reads the alpha those glyphs
              leave behind. One element cannot do both cleanly. */}
          <span className="mt-1 block italic">
            <span className="text-stellar-horizon animate-stellar-glow inline-block">
              evolve today?
            </span>
          </span>
        </h1>
        <p className="mx-auto mt-5 max-w-xl text-sm leading-relaxed text-muted-foreground sm:text-base">
          Experience the next frontier of intelligence. Create, research, and
          automate with absolute precision.
        </p>
      </div>

      {/* Input Area */}
      <div className="w-full max-w-2xl mb-6">
        {/* De pillenbalk stond hier, boven de invoer, en is op 23 augustus 2026
            vervangen door de knop in de onderste rij hieronder. De reden om hem
            ook op DIT scherm te hebben verandert niet: de eerste vraag is
            precies degene waarvoor je een project erbij haalt. */}
        <div
          className={cn(
            "glass-panel relative rounded-2xl p-3 transition-shadow",
            isDragging && "ring-2 ring-[hsl(var(--accent))]"
          )}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          {slash?.open && (
            <SlashMenu commands={slash.visible} activeIndex={slash.activeIndex} onHover={slash.setActiveIndex} onSelect={slash.select} />
          )}
          <AgentMenu menu={at} />
          <Textarea
            ref={textareaRef}
            value={input}
            onChange={(e) => { setInput(e.target.value); slash?.onChange(e.target.value); at?.onChange(); }}
            onKeyDown={handleKeyDown}
            placeholder={t("kop.opdrachten")}
            disabled={isStreaming || !hasApiKey}
            className="w-full min-h-[60px] max-h-[136px] resize-none border-0 bg-transparent focus-visible:ring-0 text-base"
            rows={2}
            data-testid="welcome-message-input"
          />
          
          {/* Attachments preview */}
          {attachments.length > 0 && (
            <div className="flex gap-2 flex-wrap mt-2 pt-2 border-t border-border/50">
              {attachments.map((att, i) => (
                <AttachmentChip key={i} att={att} compact onRemove={() => setAttachments(prev => prev.filter((_, idx) => idx !== i))} />
              ))}
            </div>
          )}

          {/* Bottom row */}
          <div className="flex items-center justify-between mt-3 pt-2 border-t border-border/50">
            <div className="flex items-center gap-1">
              <input ref={fileInputRef} type="file" accept="image/*,.pdf,.docx,.doc,.xlsx,.pptx,.txt,.md,.csv,.json,.yaml,.yml,.py,.js,.jsx,.ts,.tsx,.css,.html,.sh,.sql,.log" multiple onChange={handleFileSelect} className="hidden" />
              <PlusMenu onFiles={() => fileInputRef.current?.click()} filesDisabled={!hasApiKey}
                        connectors={connectors} rol={connectorRol} onToggleConnector={onToggleConnector}
                        onOpenSkills={onOpenSkills}
                        opties={plusOpties} onZetOptie={onZetOptie} webInInstellingen={webAccess}
                        className="h-8 w-8 rounded-lg" />
              {/* MK2 (3 nov): de modelkiezer naast Plus, ook op het welkomstscherm (de keuze wacht tot het gesprek er is). */}
              <ModelKiezer keuze={modelKeuze} standaard={standaardModel} onKies={onKiesModel} onKiesNiveau={onKiesNiveau}
                           onEigenModel={() => onOpenSettings?.("provider")} className="h-8" />
              {/* HIER STOND EEN TWEEDE KLOKJE, EN HET IS WEG op 24 augustus 2026.
                  Franks melding op glas bij v0.9.82: op het welkomstscherm stonden
                  er TWEE, een in de kop en een hier, en alleen die in de kop deed
                  iets. Zijn besluit: een klokje, in de kop, altijd op dezelfde plek.

                  **WAAROM DIE HIER STUK WAS, want dat is de eigenlijke les.** Dit
                  blok staat in `WelcomeScreen`, een ZUSTERFUNCTIE van `ChatArea`.
                  De prop `onOpenTimeMachine` is toegevoegd aan de parameterlijst
                  van `ChatArea` en niet aan die van `WelcomeScreen`, dus de naam
                  bestond hier helemaal niet.

                  En het vangnet dat ik erop dacht te hebben, ving niets: `?.`
                  beschermt tegen een parameter die BESTAAT maar leeg is, niet
                  tegen een naam die nergens is gedeclareerd. Die gooit een
                  ReferenceError, en in een klikafhandelaar leest dat als "de knop
                  doet niets". Gemeten in node:

                      onbestaande naam met ?.  -> ReferenceError
                      ongevulde parameter met ?. -> stil niets gedaan

                  De kop wordt op het welkomstscherm net zo goed getekend als
                  tijdens een gesprek, dus die ene knop dekt beide gevallen. */}
              <SwitchMenu bases={archiveBases} on={archiveOn} soort={archiveSoort}
                          onToggle={onToggleArchive} onAllOff={onAllArchiveOff} onAllOn={onAllArchiveOn} />
            </div>
            
            <div className="flex items-center gap-2">
              {/* GLASRONDE 0.9.238 (Frank 3 nov): hier stond een tweede modelkiezer, die het STANDAARDMODEL van het
                  account zette. Er is er nu een: de kiezer van MK2 naast Plus (per gesprek). Het standaardmodel blijft
                  te kiezen in Instellingen > Aanbieder en met /model. */}
              
              {/* Same council control as the in-conversation composer. It used
                  to exist only there, so the one place you cannot reach it was
                  the screen you start every conversation on. */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    type="button"
                    variant={councilMode ? "default" : "ghost"}
                    size="icon"
                    disabled={isStreaming || !hasApiKey || councilUnavailable}
                    className={cn("tap-target h-8 w-8 rounded-lg", councilMode ? "bg-nebula hover:bg-nebula/80 text-[hsl(var(--nebula-foreground))]" : "text-muted-foreground")}
                    title={councilTitle}
                    aria-label={councilTitle}
                    data-testid="welcome-council-toggle-btn"
                  >
                    <Landmark className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48">
                  <DropdownMenuItem onClick={() => onSetCouncilMode?.(null)}>
                    {!councilMode && <Check className="h-3 w-3 mr-2" />}<span className={councilMode ? "ml-5" : ""}>{t("Raad uit")}</span>
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => onSetCouncilMode?.("synthesis")}>
                    {councilMode === "synthesis" && <Check className="h-3 w-3 mr-2" />}<span className={councilMode !== "synthesis" ? "ml-5" : ""}>{t("Synthese")}</span>
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => onSetCouncilMode?.("consensus")}>
                    {councilMode === "consensus" && <Check className="h-3 w-3 mr-2" />}<span className={councilMode !== "consensus" ? "ml-5" : ""}>{t("Consensus (beoordeeld)")}</span>
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => onSetCouncilMode?.("best_of_n")}>
                    {councilMode === "best_of_n" && <Check className="h-3 w-3 mr-2" />}<span className={councilMode !== "best_of_n" ? "ml-5" : ""}>{t("Beste van N")}</span>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>

              {/* The one primary CTA on this screen, so the one place
                  Supernova is allowed. It sat on Solar Flare, which the guide
                  reserves for the hover state, meaning the button was already
                  showing its own hover before anybody touched it. */}
              <Button onClick={onSubmit} size="icon" disabled={(!input.trim() && attachments.length === 0) || isStreaming || !hasApiKey} className="tap-target h-8 w-8 rounded-lg bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] hover:bg-[hsl(var(--accent))]" data-testid="welcome-send-btn">
                <Send className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Suggestion Buttons */}
      {hasApiKey ? (
        <div className="flex flex-wrap justify-center gap-2 max-w-2xl">
          {Object.entries(SUGGESTIONS).map(([key, category]) => {
            const Icon = category.icon;
            return (
              <DropdownMenu key={key}>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" className="h-9 gap-2 rounded-full border-border/50 hover:bg-muted" data-testid={`suggestion-${key}`}>
                    <Icon className="h-4 w-4" />
                    {t(category.label)}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="center" className="w-72">
                  {category.items.map((item, i) => (
                    <DropdownMenuItem key={i} onClick={() => onSuggestionClick(t(item))} className="cursor-pointer">
                      {t(item)}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            );
          })}
        </div>
      ) : (
        <div className="bg-[hsl(var(--muted))] rounded-xl p-6 max-w-md">
          <h3 className="font-semibold mb-2">{t("Aan de slag")}</h3>
          <p className="text-sm text-muted-foreground mb-4">{t("Stel je API-sleutel in of gebruik Ollama voor lokale modellen.")}</p>
          <Button onClick={onOpenSettings} className="gap-2" data-testid="welcome-configure-btn">{t("Aanbieder instellen")}</Button>
        </div>
      )}

      {/* Scanning, from the screen everybody starts on. It is here rather than
          buried in settings because it is how a second device joins: point the
          camera at the code the other one is showing. The same entry serves a
          phone and a desktop, and it is the one the SPEAQ ID sign-in will use
          next. */}
      <button
        type="button"
        onClick={() => navigate("/scan")}
        className="tap-target mt-8 inline-flex items-center gap-2 text-xs text-muted-foreground hover:text-foreground transition-colors"
        data-testid="welcome-scan-btn"
      >
        <QrCode className="h-4 w-4" />
        {t("Scan een code")}
      </button>
    </div>
  );
}
